// Provider exchange strings → Google Finance / TradingView symbol prefixes (design.md §8).

export type ExchangeCode = 'NASDAQ' | 'NYSE' | 'NYSEARCA' | 'NYSEAMERICAN' | 'BATS' | 'TSE' | 'CVE' | 'CNSX' | 'NEO' | null;

const NASDAQ_MICS = new Set(['XNAS', 'XNMS', 'XNGS', 'XNCM']);

export function normalizeExchange(raw?: string): ExchangeCode {
  if (!raw) return null;
  const s = raw.toUpperCase();
  // Canadian venues first: 'CBOE CANADA' would otherwise match the US Cboe check below.
  if (s === 'XTSE' || s === 'TSX' || s === 'TSE' || s.includes('TORONTO')) return 'TSE';
  if (s === 'XTSX' || s === 'TSXV' || s === 'CVE' || s.includes('VENTURE')) return 'CVE';
  if (s === 'XCNQ' || s === 'CSE' || s === 'CNSX' || s.includes('CANADIAN SECURITIES')) return 'CNSX';
  if (s === 'NEOE' || s === 'NEO' || s.includes('CBOE CANADA') || s.includes('AEQUITAS')) return 'NEO';
  if (s.includes('NASDAQ') || NASDAQ_MICS.has(s)) return 'NASDAQ';
  if (s.includes('ARCA') || s === 'ARCX') return 'NYSEARCA';
  if (s.includes('BATS') || s.includes('CBOE') || s === 'BATS') return 'BATS';
  if (s.includes('NYSE AMERICAN') || s.includes('NYSE MKT') || s === 'XASE') return 'NYSEAMERICAN';
  if (s.includes('NEW YORK') || s.includes('NYSE') || s === 'XNYS') return 'NYSE';
  return null;
}

const LABELS: Record<NonNullable<ExchangeCode>, string> = { NASDAQ: 'NASDAQ', NYSE: 'NYSE', NYSEARCA: 'NYSE Arca', NYSEAMERICAN: 'NYSE American', BATS: 'Cboe', TSE: 'TSX', CVE: 'TSXV', CNSX: 'CSE', NEO: 'Cboe Canada' };
// TradingView prefixes for the Canadian venues (Google Finance uses the codes above directly).
const TRADINGVIEW_CA: Partial<Record<NonNullable<ExchangeCode>, string>> = { TSE: 'TSX', CVE: 'TSXV', CNSX: 'CSE', NEO: 'NEO' };

/** Short display name for a provider exchange string or MIC; OTC venues collapse to "OTC", unknown values pass through. */
export function exchangeLabel(raw?: string): string | undefined {
  if (!raw) return undefined;
  const code = normalizeExchange(raw);
  if (code) return LABELS[code];
  const s = raw.toUpperCase();
  if (s.startsWith('OTC') || s === 'OOTC' || s.startsWith('PIN')) return 'OTC';
  if (s === 'IEXG') return 'IEX';
  return raw;
}

export function googleFinanceUrl(symbol: string, exchange?: string): string {
  const code = normalizeExchange(exchange);
  const sym = encodeURIComponent(symbol.toUpperCase());
  return code ? `https://www.google.com/finance/quote/${sym}:${code}` : `https://www.google.com/finance/quote/${sym}`;
}

export function tradingViewSymbol(symbol: string, exchange?: string, assetClass?: string): string {
  const sym = symbol.toUpperCase();
  if (assetClass === 'crypto') return `COINBASE:${sym}USD`;
  const code = normalizeExchange(exchange);
  if (code === 'NYSEARCA' || code === 'BATS' || code === 'NYSEAMERICAN') return `AMEX:${sym}`;
  if (code && TRADINGVIEW_CA[code]) return `${TRADINGVIEW_CA[code]}:${sym}`;
  if (code) return `${code}:${sym}`;
  return sym;
}

/** Google Finance quote page for a mutual fund: Canadian FundSERV codes live under MUTF_CA, US funds under MUTF. */
export function mutualFundUrl(code: string, market?: string): string {
  return `https://www.google.com/finance/quote/${encodeURIComponent(code.toUpperCase())}:${market === 'CA' ? 'MUTF_CA' : 'MUTF'}`;
}
